import dayjs from 'dayjs';
import { BudgetModel } from '../../../../Generated/Commodity-Management-Api';

export const BASE_BUDGET_ROUND_NAME = 'Base';

/**
 * Pick the budget row that represents the current forecast for a single budget
 * month: the row from the most recently entered budget round that actually has
 * a value for that month, falling back to the Base round.
 *
 * This is deliberately resolved per month rather than by picking one global
 * "latest entryDate" and filtering on it. A round entered for only part of the
 * year (a single revised month, say) would otherwise wipe out every other
 * month, because no other row carries that exact entryDate.
 */
export function getCurrentForecastForMonth(budgets: BudgetModel[], year: number, month: number): BudgetModel | undefined {
  const forMonth = budgets.filter(b => b.budgetDate?.year === year && b.budgetDate?.month === month);
  if (forMonth.length === 0) return undefined;

  const latest = forMonth.reduce<BudgetModel | undefined>((best, budget) => {
    const entryDate = budget.budgetRoundModel?.entryDate;
    if (!entryDate) return best;
    if (!best) return budget;
    return dayjs(entryDate).isAfter(dayjs(best.budgetRoundModel!.entryDate)) ? budget : best;
  }, undefined);

  return latest ?? forMonth.find(b => b.budgetRoundModel?.budgetRoundName === BASE_BUDGET_ROUND_NAME) ?? forMonth[0];
}

/** The Base-round volume for a budget month, summed across matching rows. */
export function getBaseVolumeForMonth(budgets: BudgetModel[], year: number, month: number): number {
  return budgets
    .filter(
      b =>
        b.budgetDate?.year === year &&
        b.budgetDate?.month === month &&
        b.budgetRoundModel?.budgetRoundName === BASE_BUDGET_ROUND_NAME,
    )
    .reduce((sum, b) => sum + (b.volume ?? 0), 0);
}
